import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"

import CatchCard from "~/library/CatchCard"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikSelect from "~/library/FormikSelect"
import SelectOrCreate from "~/library/SelectOrCreate"
import LoadingWrapper from "~/library/LoadingWrapper"

import * as Yup from "yup"
import type Deal from "~/types/Deal"
import type DealType from "~/types/DealType"
import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceAccounts, apiWorkspaceContacts, apiWorkspaceSalesReps, apiWorkspaceDealTypes } from "~/routes"

const dealSchema = Yup.object({
    name: Yup.string().required("Deal name is required"),
    dealTypeId: Yup.string().required("Deal type is required"),
    dealStageId: Yup.string().required("Deal stage is required"),
    value: Yup.number().min(0, "Must be positive").required("Value is required"),
    probability: Yup.number().min(0, "Must be 0 or greater").max(100, "Must be 100 or less"),
    expectedCloseDate: Yup.date().required("Expected close date is required"),
})

interface DealFormProps {
    onSubmit: (values: any) => void
    initialValues?: any
    isEditing?: boolean
    dealStages: Array<{ id: string; name: string }>
    initialAccount?: { id: string; name: string } | null
    initialContacts?: Array<{ id: string; firstName: string; lastName: string }> | null
}

const DealForm: React.FC<DealFormProps> = ({
                                               onSubmit,
                                               initialValues,
                                               isEditing = false,
                                               dealStages,
                                               initialAccount = null,
                                               initialContacts = null,
                                           }) => {
    const { venueSlug } = getAppConfig()

    const [accountSearch, setAccountSearch] = useState("")
    const [accountResults, setAccountResults] = useState<Array<{ id: string; name: string }>>([])
    const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(initialAccount)
    const [isSearchingAccount, setIsSearchingAccount] = useState(false)
    const [showAccountDropdown, setShowAccountDropdown] = useState(false)

    const [contactSearch, setContactSearch] = useState("")
    const [contactResults, setContactResults] = useState<Array<{ id: string; firstName: string; lastName: string }>>([])
    const [selectedContacts, setSelectedContacts] = useState<Array<{ id: string; firstName: string; lastName: string }>>(initialContacts || [])
    const [isSearchingContact, setIsSearchingContact] = useState(false)
    const [showContactDropdown, setShowContactDropdown] = useState(false)

    const salesRepsEndpoint = apiWorkspaceSalesReps.index.path({ venue_slug: venueSlug })

    const { data: dealTypes, isLoading: typesLoading } = useFetchResource<
        { deal_types: DealType[] },
        "deal_types"
        >(
        apiWorkspaceDealTypes.index.path({ venue_slug: venueSlug }),
        "deal_types",
        {}
    )

    const defaultValues = {
        name: "",
        dealTypeId: "",
        dealStageId: dealStages.length > 0 ? dealStages[0].id : "",
        salesRepId: "",
        salesRepAttributes: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
        },
        value: "",
        probability: "",
        expectedCloseDate: "",
    }

    useEffect(() => {
        if (accountSearch.length >= 2) {
            setIsSearchingAccount(true)
            const timeoutId = setTimeout(async () => {
                try {
                    const url = apiWorkspaceAccounts.index.path({ venue_slug: venueSlug })
                    const response = await fetch(`${url}?q[name_cont]=${encodeURIComponent(accountSearch)}`)
                    const data = await response.json()
                    setAccountResults(data.accounts || [])
                    setShowAccountDropdown(true)
                } catch (error) {
                    console.error("Error searching accounts:", error)
                } finally {
                    setIsSearchingAccount(false)
                }
            }, 300)

            return () => clearTimeout(timeoutId)
        } else {
            setAccountResults([])
            setShowAccountDropdown(false)
        }
    }, [accountSearch, venueSlug])

    useEffect(() => {
        if (contactSearch.length >= 2 && selectedAccount) {
            setIsSearchingContact(true)
            const timeoutId = setTimeout(async () => {
                try {
                    const url = apiWorkspaceContacts.index.path({ venue_slug: venueSlug })
                    const response = await fetch(
                        `${url}?q[account_id_eq]=${selectedAccount.id}&q[first_name_or_last_name_cont]=${encodeURIComponent(contactSearch)}`
                    )
                    const data = await response.json()
                    setContactResults(data.contacts || [])
                    setShowContactDropdown(true)
                } catch (error) {
                    console.error("Error searching contacts:", error)
                } finally {
                    setIsSearchingContact(false)
                }
            }, 300)

            return () => clearTimeout(timeoutId)
        } else {
            setContactResults([])
            setShowContactDropdown(false)
        }
    }, [contactSearch, selectedAccount, venueSlug])

    const handleFormSubmit = (values: any) => {
        const formData = {
            name: values.name,
            deal_type_id: values.dealTypeId,
            deal_stage_id: values.dealStageId,
            sales_rep_id: values.salesRepId || null,
            sales_rep_attributes: values.salesRepId ? undefined : values.salesRepAttributes,
            value: values.value,
            probability: values.probability,
            expected_close_date: values.expectedCloseDate,
            account_id: selectedAccount?.id || null,
            contact_ids: selectedContacts.map(c => c.id),
        }
        onSubmit(formData)
    }

    const dealTypeOptions = dealTypes?.map((type) => ({
        value: type.id,
        label: type.name,
    })) || []

    const dealStageOptions = dealStages.map((stage) => ({
        value: stage.id,
        label: stage.name,
    }))

    const handleRemoveContact = (contactId: string) => {
        setSelectedContacts(selectedContacts.filter(c => c.id !== contactId))
    }

    return (
        <LoadingWrapper isLoading={typesLoading}>
            <FormikWrapper
                onSubmit={handleFormSubmit}
                validationSchema={dealSchema}
                initialValues={isEditing ? initialValues : defaultValues}
            >
                <CatchCard title="Deal Information">
                    <FormikInput
                        name="name"
                        label="Deal Name"
                        placeholder="Q1 2024 Sponsorship Deal"
                        required
                        helpText=""
                    />

                    <div className="row">
                        <div className="col-6">
                            <FormikSelect
                                name="dealTypeId"
                                label="Deal Type"
                                options={dealTypeOptions}
                                required
                                helpText=""
                            />
                        </div>
                        <div className="col-6">
                            <FormikSelect
                                name="dealStageId"
                                label="Deal Stage"
                                options={dealStageOptions}
                                required
                                helpText=""
                                noEmptyOption={true}
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">
                            Sales Representative
                        </label>

                        <SelectOrCreate
                            name="salesRepId"
                            attributesPrefix="salesRepAttributes"
                            placeholder="Search or Create a Sales Representative"
                            endpoint={salesRepsEndpoint}
                            responseKey="sales_reps"
                            labelKey="fullName"
                            searchParam="first_name_or_last_name_cont"
                            allowCreate={!isEditing}
                            createFields={[
                                { name: "firstName", label: "First Name", type: "text", required: true },
                                { name: "lastName", label: "Last Name", type: "text", required: true },
                                { name: "email", label: "Email", type: "email", required: true },
                                { name: "phone", label: "Phone", type: "text" },
                            ]}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">
                            Account <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search for an account..."
                                value={selectedAccount ? selectedAccount.name : accountSearch}
                                onChange={(e) => {
                                    setAccountSearch(e.target.value)
                                    if (selectedAccount) {
                                        setSelectedAccount(null)
                                        setSelectedContacts([])
                                    }
                                }}
                                onFocus={() => {
                                    if (accountResults.length > 0) {
                                        setShowAccountDropdown(true)
                                    }
                                }}
                            />
                            {isSearchingAccount && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                    <span className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </span>
                                </div>
                            )}
                            {showAccountDropdown && accountResults.length > 0 && (
                                <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {accountResults.map((account) => (
                                        <button
                                            key={account.id}
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => {
                                                setSelectedAccount(account)
                                                setAccountSearch("")
                                                setShowAccountDropdown(false)
                                            }}
                                        >
                                            {account.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {selectedAccount && (
                                <div className="mt-2">
                                    <span className="badge bg-primary me-2">{selectedAccount.name}</span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-danger p-0"
                                        onClick={() => {
                                            setSelectedAccount(null)
                                            setAccountSearch("")
                                            setSelectedContacts([])
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="form-text">Required: Select the account for this deal</div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">
                            Contacts
                        </label>
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={selectedAccount ? "Search for contacts..." : "Select an account first"}
                                value={contactSearch}
                                onChange={(e) => setContactSearch(e.target.value)}
                                onFocus={() => {
                                    if (contactResults.length > 0) {
                                        setShowContactDropdown(true)
                                    }
                                }}
                                disabled={!selectedAccount}
                            />
                            {isSearchingContact && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                    <span className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </span>
                                </div>
                            )}
                            {showContactDropdown && contactResults.length > 0 && (
                                <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {contactResults.map((contact) => (
                                        <button
                                            key={contact.id}
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => {
                                                if (!selectedContacts.find(c => c.id === contact.id)) {
                                                    setSelectedContacts([...selectedContacts, contact])
                                                }
                                                setContactSearch("")
                                                setShowContactDropdown(false)
                                            }}
                                        >
                                            {contact.firstName} {contact.lastName}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {selectedContacts.length > 0 && (
                                <div className="mt-2">
                                    {selectedContacts.map((contact) => (
                                        <span key={contact.id} className="badge bg-success me-2 mb-2">
                                            {contact.firstName} {contact.lastName}
                                            <button
                                                type="button"
                                                className="btn-close btn-close-white ms-2"
                                                style={{ fontSize: '0.6rem' }}
                                                onClick={() => handleRemoveContact(contact.id)}
                                                aria-label="Remove"
                                            />
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="form-text">Optional: Add contacts from the selected account</div>
                    </div>
                </CatchCard>

                <CatchCard title="Deal Details">
                    <div className="row">
                        <div className="col-4">
                            <FormikInput
                                name="value"
                                label="Deal Value"
                                type="number"
                                placeholder="50000.00"
                                required
                                helpText="Total value of the deal"
                            />
                        </div>
                        <div className="col-4">
                            <FormikInput
                                name="probability"
                                label="Probability (%)"
                                type="number"
                                placeholder="75"
                                helpText="Likelihood of closing (0-100)"
                            />
                        </div>
                        <div className="col-4">
                            <FormikInput
                                name="expectedCloseDate"
                                label="Expected Close Date"
                                type="date"
                                required
                                helpText=""
                            />
                        </div>
                    </div>
                </CatchCard>

                <div className="d-flex">
                    <div className="ms-auto">
                        <Link className="btn btn-secondary me-2" to="/deals">
                            Cancel
                        </Link>
                        <button type="submit" className="btn btn-primary">
                            {isEditing ? "Update Deal" : "Create Deal"}
                        </button>
                    </div>
                </div>
            </FormikWrapper>
        </LoadingWrapper>
    )
}

export default DealForm
